import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import Form, { FormInstance } from "antd/es/form";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import SchemaFieldType from "@mapmycustomers/shared/enum/SchemaFieldType";
import { Deal, EntityType } from "@mapmycustomers/shared/types/entity";
import DealLossReason from "@mapmycustomers/shared/types/entity/deals/DealLossReason";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import User from "@mapmycustomers/shared/types/User";
import { Button, DatePicker, NumberField } from "@mapmycustomers/ui";

import ConditionalFormField, {
  ConditionalFormFieldRenderProps,
} from "@app/component/ConditionalFormField";
import FormFields from "@app/component/FormFields";
import {
  CompanyRelationshipField,
  PersonRelationshipField,
} from "@app/component/FormFields/components/Associations";
import DealLossCommentField from "@app/component/FormFields/components/DealLossCommentField";
import DealLossReasonField from "@app/component/FormFields/components/DealLossReasonField";
import FormLayoutSelector from "@app/component/FormFields/components/FormLayoutSelector";
import FunnelStageField from "@app/component/FormFields/components/FunnelStageField";
import OwnerField from "@app/component/FormFields/components/OwnerField";
import FormNoLayoutAlert from "@app/component/FormNoLayoutAlert";
import SaveChangesFooter from "@app/component/preview/components/SaveChangesFooter";
import showCannotEditNotification from "@app/component/preview/util/showCannotEditNotification";
import showConfirmationIfOwnerChanged from "@app/component/preview/util/showConfirmationIfOwnerChanged";
import useBlankRequiredPrivateFields from "@app/component/preview/util/useBlankRequiredPrivateFieldsChecker";
import RelatedEntitiesMatching from "@app/component/RelatedEntitiesMatching";
import DealClosedDateSettingValue from "@app/enum/DealClosedDateSettingValue";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import AssociationsState from "@app/store/associations/AssociationsState";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { ensureStagesFetched } from "@app/store/deal/actions";
import { fetchDealLossReasons } from "@app/store/dealLossReasons/actions";
import { getDealLossReasons } from "@app/store/dealLossReasons/selectors";
import {
  getOrganizationSettingValue,
  isCurrentUserOwner,
  isCurrentUserReadOnly,
} from "@app/store/iam";
import { getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { findDifference } from "@app/util/arrayUtils";
import { MAX_DEAL_SCORE } from "@app/util/consts";
import showClosingDateAlert from "@app/util/deal/showClosingDateAlert";
import eventEmitter from "@app/util/eventEmitter";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import dealToFormValues from "@app/util/form/dealToFormValues";
import EditDealFormValues from "@app/util/form/EditDealFormValues";
import { formatDate } from "@app/util/formatters";
import useAllowedUsers from "@app/util/hook/useAllowedUsers";
import useCustomFieldValidation from "@app/util/hook/useCustomFieldValidation";
import useOnScreen from "@app/util/hook/useOnScreen";
import useSharedRef from "@app/util/hook/useSharedRef";
import getActualLayoutSchema from "@app/util/layout/getActualLayoutSchema";
import { dealLayoutModel } from "@app/util/layout/impl";

import AdditionalDetails from "../../components/AdditionalDetails";
import DeleteButton from "../../components/DeleteButton";
import HistoryButton from "../../components/HistoryButton";
import TabLayout from "../../components/TabLayout";
import consts from "../../styles/_consts.module.scss";
import useDividerSpan from "../../util/useDividerSpan";
import {
  changeAssociatedEntities,
  deleteDeal,
  relateUnrelateEntities,
  updateDeal,
} from "../store/actions";
import { isDeleting, isEntityRelating, isEntityRelatingSuccessful } from "../store/selectors";

import styles from "./Details.module.scss";

const excludeUnnecessaryFields = (schemaField: LayoutSchemaField) =>
  schemaField.fieldType !== SchemaFieldType.FILE;
const allOfThem = () => true;

const messages = defineMessages({
  saveChanges: {
    id: "dealRecordPane.details.saveButton.changed",
    defaultMessage: "Save Changes",
    description: "Save button label if form changed",
  },
  saved: {
    id: "dealRecordPane.saveButton.notChanged",
    defaultMessage: "Saved",
    description: "Save button label if form did not changed",
  },
  scorePlaceholder: {
    id: "createDealModal.field.score.placeholder",
    defaultMessage: "0-{max}",
    description: "Score field's placeholder on the Create Deal modal",
  },
  title: {
    id: "dealRecordPane.details.title",
    defaultMessage: "Details",
    description: "Deal details tab title",
  },
});

interface Props {
  allOrgUsers: User[];
  allUsersAllowedForOwnership: boolean;
  associations: AssociationsState;
  closedDateUpdateSetting: DealClosedDateSettingValue;
  deal: Deal;
  dealUpdateLoading: boolean;
  deleting?: boolean;
  detailsForm: FormInstance<EditDealFormValues>;
  ensureStagesFetched: typeof ensureStagesFetched.request;
  entityRelatingSuccess: boolean;
  fetchDealLossReasons: typeof fetchDealLossReasons.request;
  formFieldsRef: MutableRefObject<any>;
  funnels: Funnel[];
  funnelStages: Record<Funnel["id"], Stage[]>;
  handleFinishFail: () => void;
  hasChanges: boolean;
  isEntityRelating: boolean;
  isOwner: boolean;
  isReadOnlyMember: boolean;
  lossReasons: DealLossReason[];
  onChangeAssociatedEntities: typeof changeAssociatedEntities.request;
  onDelete: typeof deleteDeal.request;
  onFormChange: (isChanged: boolean) => void;
  onHide: () => void;
  onUpdateDeal: typeof updateDeal.request;
  relateUnrelateEntities: typeof relateUnrelateEntities.request;
}

const Details: React.FC<Props> = ({
  allOrgUsers,
  allUsersAllowedForOwnership,
  associations,
  closedDateUpdateSetting,
  deal,
  dealUpdateLoading,
  deleting,
  detailsForm,
  ensureStagesFetched,
  entityRelatingSuccess,
  fetchDealLossReasons,
  formFieldsRef,
  funnels,
  funnelStages,
  handleFinishFail,
  hasChanges,
  isEntityRelating,
  isOwner,
  isReadOnlyMember,
  lossReasons,
  onChangeAssociatedEntities,
  onDelete,
  onFormChange,
  onHide,
  onUpdateDeal,
  relateUnrelateEntities,
}) => {
  useEffect(() => {
    ensureStagesFetched(undefined);
    fetchDealLossReasons();
  }, [ensureStagesFetched, fetchDealLossReasons]);

  const entityLayout = useMemo(() => dealLayoutModel.getLayoutFor(deal), [deal]);
  const [formLayout, setFormLayout] = useState(entityLayout);
  useEffect(() => {
    setFormLayout(entityLayout);
  }, [entityLayout]);

  const intl = useIntl();
  const saveButtonRef = useRef<HTMLElement>(null);
  const [funnelId, setFunnelId] = useState<Funnel["id"]>(deal.funnel.id);
  const stages = useMemo(() => funnelStages[funnelId] ?? [], [funnelId, funnelStages]);

  const noAssociations = useMemo(
    () => !associations.associatedCompany && !associations.associatedPerson,
    [associations.associatedCompany, associations.associatedPerson]
  );

  const blankRequiredPrivateFields = useBlankRequiredPrivateFields(
    getActualLayoutSchema(formLayout, {
      funnelId: deal.funnel.id,
      stageId: deal.stage.id,
    }),
    EntityType.DEAL,
    deal
  );
  const [cannotSave, setCannotSave] = useState(false);

  const handleCustomFieldValidate = useCustomFieldValidation(detailsForm, handleFinishFail);

  const handleFormChange = useCallback(() => {
    const { funnelId } = detailsForm.getFieldsValue();
    setFunnelId(funnelId);
    onFormChange(detailsForm.isFieldsTouched());
    if (blankRequiredPrivateFields.length) {
      setCannotSave(true);
      showCannotEditNotification(intl, EntityType.DEAL, blankRequiredPrivateFields);
    }
  }, [blankRequiredPrivateFields, detailsForm, intl, onFormChange, setFunnelId]);

  const handleUndo = useCallback(() => {
    detailsForm.resetFields();
    onFormChange(false);
  }, [detailsForm, onFormChange]);

  const handleSave = useCallback(() => detailsForm.submit(), [detailsForm]);
  const handleFormFinish = useCallback(
    (formData) => {
      const ownerChanged = deal.user.id !== formData.user;
      showConfirmationIfOwnerChanged(intl, ownerChanged, isOwner, () => {
        const [groupIdsToAdd, groupIdsToRemove] = findDifference(
          (deal.groups ?? []).map(({ id }) => id),
          Array.from(formData.groups ?? [])
        );

        const dealStage = stages.find(({ id }) => id === formData.stageId)!;
        const today = new Date();

        const payload = {
          callback: (updatedDeal: Deal) => {
            onFormChange(false);

            const updatedFormValues = dealToFormValues(updatedDeal);
            const customFields = updatedFormValues.customFields;
            const customFormFields = Object.keys(customFields ?? {}).map((key) => ({
              errors: [],
              name: ["customFields", key],
              touched: false,
              value: customFields?.[key as keyof EditDealFormValues["customFields"]],
            }));
            const fields = Object.keys(updatedFormValues).map((key) => ({
              name: key,
              touched: false,
              value: updatedFormValues[key as keyof EditDealFormValues],
            }));
            // only update field values without touching them as setFieldsValue touches all fields
            detailsForm.setFields([...fields, ...customFormFields]);

            if (ownerChanged && !isOwner) {
              onHide();
              return;
            }
          },
          customFields: formData.customFields,
          customFieldsValueValidateCallback: handleCustomFieldValidate,
          deal: {
            ...deal,
            account: formData.companyId ? { id: formData.companyId } : null,
            amount: formData.amount ?? null,
            cadenceInterval: formData.cadenceInterval,
            closingDate: formData.closingDate
              ? closedDateUpdateSetting === DealClosedDateSettingValue.CHANGE_TO_TODAY &&
                dealStage.type !== DealStageType.DEFAULT
                ? formatDate(today, "yyyy-MM-dd")
                : formatDate(formData.closingDate, "yyyy-MM-dd")
              : null,
            contact: formData.personId ? { id: formData.personId } : null,
            crmActivityTypeId: formData.crmActivityTypeId,
            dealLossComment: formData.dealLossComment,
            dealLossReason: formData.dealLossReasonId
              ? lossReasons.find(({ id }) => id === formData.dealLossReasonId) ?? null
              : null,
            funnel: funnels.find(({ id }) => id === formData.funnelId)!,
            name: formData.name,
            score: formData.score,
            stage: dealStage,
            user: { id: formData.user },
          },
          groupIdsToAdd,
          groupIdsToRemove,
          layoutId: formLayout.id,
          ownerChanged,
        };

        if (
          closedDateUpdateSetting === DealClosedDateSettingValue.ASK_EVERYTIME &&
          dealStage.type !== DealStageType.DEFAULT &&
          deal.stage.id !== dealStage.id &&
          deal.closingDate
        ) {
          showClosingDateAlert(
            () => {
              onUpdateDeal({
                ...payload,
                deal: { ...payload.deal, closingDate: formatDate(today, "yyyy-MM-dd") },
              });
            },
            () => {
              onUpdateDeal(payload);
            },
            intl
          );
        } else {
          onUpdateDeal(payload);
        }
      });
    },
    [
      deal,
      stages,
      lossReasons,
      funnels,
      onHide,
      formLayout.id,
      closedDateUpdateSetting,
      onUpdateDeal,
      isOwner,
      onFormChange,
      detailsForm,
      intl,
      handleCustomFieldValidate,
    ]
  );

  useEffect(() => {
    setFunnelId(deal.funnel.id);
  }, [deal.funnel.id]);

  useEffect(() => {
    detailsForm.setFieldsValue({ stageId: deal.stage?.id });
  }, [deal.stage?.id, detailsForm]);

  useEffect(() => {
    onFormChange(false);
    detailsForm.resetFields();
    detailsForm.setFieldsValue(dealToFormValues(deal));
  }, [deal, detailsForm, onFormChange]);

  const isSaveButtonVisible = useOnScreen(saveButtonRef);

  // This is impossible to set span dynamically yet, so we have to use JS solution to calculate it
  // There's a discussion in W3C to support that eventually: https://github.com/w3c/csswg-drafts/issues/1373
  const threshold = 2 * parseInt(consts.breakpointDetailsHalf) + parseInt(consts.gridGap);
  const [dividerSpan, containerRef] = useDividerSpan(threshold);

  const getRowClassName = useCallback(
    (schemaField?: LayoutSchemaField) => {
      const isDivider = !schemaField || schemaField.fieldType === SchemaFieldType.DIVIDER;
      return cn(styles.rowClassName, {
        [styles.dividerRowSpan2]: isDivider && dividerSpan === 2,
        [styles.dividerRowSpan3]: isDivider && dividerSpan === 3,
      });
    },
    [dividerSpan]
  );

  const handleDelete = useCallback(() => {
    handleUndo();
    onDelete();
  }, [handleUndo, onDelete]);

  useEffect(() => {
    eventEmitter.on("saveDealChanges", handleSave);
    return () => {
      eventEmitter.off("saveDealChanges", handleSave);
    };
  }, [handleSave]);

  const ref = useSharedRef(formFieldsRef, containerRef);
  const layoutsLength = dealLayoutModel.layouts.length;
  const allowedOwnerUsers = useAllowedUsers(allOrgUsers, deal, allUsersAllowedForOwnership);

  return (
    <TabLayout
      action={
        <Button
          disabled={!hasChanges || cannotSave}
          loading={dealUpdateLoading}
          onClick={handleSave}
          ref={saveButtonRef}
          type="primary"
        >
          {hasChanges
            ? intl.formatMessage(messages.saveChanges)
            : intl.formatMessage(messages.saved)}
        </Button>
      }
      hasChanges={hasChanges}
      title={intl.formatMessage(messages.title)}
    >
      <div ref={ref}>
        {!isReadOnlyMember && (
          <RelatedEntitiesMatching
            associatedCompany={associations.associatedCompany}
            associatedPerson={associations.associatedPerson}
            entityRelatingSuccess={entityRelatingSuccess}
            isEntityRelating={isEntityRelating}
            relateUnrelateEntities={relateUnrelateEntities}
          />
        )}
        {layoutsLength > 1 && (
          <FormLayoutSelector
            dividerClassName={styles.divider}
            entityType={EntityType.DEAL}
            onChange={setFormLayout}
            value={formLayout}
          />
        )}
        <Form
          form={detailsForm}
          initialValues={dealToFormValues(deal)}
          layout="vertical"
          onFieldsChange={handleFormChange}
          onFinish={handleFormFinish}
          onFinishFailed={handleFinishFail}
          preserve
        >
          {formLayout ? (
            <FormFields
              appendAddableFieldsWithFilter={allOfThem}
              autoFocus={false}
              className={styles.fieldsContainer}
              disableAllFields={blankRequiredPrivateFields.length > 0 || isReadOnlyMember}
              disallowAdding
              entityType={EntityType.DEAL}
              filterFields={excludeUnnecessaryFields}
              layout={formLayout}
              rowClassName={getRowClassName}
            >
              <ConditionalFormField feature={FieldFeature.FUNNEL_FIELD}>
                <FunnelStageField />
              </ConditionalFormField>

              <ConditionalFormField fieldName={DealFieldName.DEAL_LOSS_REASON}>
                <DealLossReasonField />
              </ConditionalFormField>

              <ConditionalFormField fieldName={DealFieldName.DEAL_LOSS_COMMENT}>
                <DealLossCommentField />
              </ConditionalFormField>

              <ConditionalFormField fieldName={DealFieldName.SCORE}>
                {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                  <Form.Item label={label} name="score" required={required} rules={[{ required }]}>
                    <NumberField
                      inputClassName={styles.fullWidth}
                      locked={disabled}
                      max={MAX_DEAL_SCORE}
                      min={0}
                      placeholder={intl.formatMessage(messages.scorePlaceholder, {
                        max: MAX_DEAL_SCORE,
                      })}
                      precision={0}
                    />
                  </Form.Item>
                )}
              </ConditionalFormField>

              <ConditionalFormField fieldName={DealFieldName.CLOSING_DATE}>
                {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                  <Form.Item
                    label={label}
                    name="closingDate"
                    required={required}
                    rules={[{ required }]}
                  >
                    <DatePicker
                      allowClear
                      className={styles.fullWidth}
                      format="PP"
                      locked={disabled}
                    />
                  </Form.Item>
                )}
              </ConditionalFormField>

              <ConditionalFormField fieldName={DealFieldName.COMPANY}>
                {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                  <CompanyRelationshipField
                    allowClear
                    associatedCompany={associations.associatedCompany}
                    associatedDeal={associations.associatedDeal}
                    associatedPerson={associations.associatedPerson}
                    disabled={disabled}
                    entityType={EntityType.DEAL}
                    label={label}
                    onChangeAssociatedEntities={onChangeAssociatedEntities}
                    required={required}
                    suggestedCompanies={
                      noAssociations ? undefined : associations.availableCompanies
                    }
                  />
                )}
              </ConditionalFormField>

              <ConditionalFormField fieldName={DealFieldName.PERSON}>
                {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                  <PersonRelationshipField
                    allowClear
                    associatedCompany={associations.associatedCompany}
                    associatedDeal={associations.associatedDeal}
                    associatedPerson={associations.associatedPerson}
                    disabled={disabled}
                    label={label}
                    onChangeAssociatedEntities={onChangeAssociatedEntities}
                    required={required}
                    suggestedPeople={noAssociations ? undefined : associations.availablePeople}
                  />
                )}
              </ConditionalFormField>

              <ConditionalFormField fieldName={DealFieldName.USER}>
                {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                  <Form.Item
                    label={label}
                    name={DealFieldName.USER}
                    required={required}
                    rules={[{ required }]}
                  >
                    <OwnerField disabled={disabled} user={deal?.user} users={allowedOwnerUsers} />
                  </Form.Item>
                )}
              </ConditionalFormField>
            </FormFields>
          ) : (
            <FormNoLayoutAlert />
          )}
        </Form>
        <AdditionalDetails entity={deal} />
        <DeleteButton deleting={deleting} entityType={EntityType.DEAL} onDelete={handleDelete} />
        {!cannotSave && (
          <SaveChangesFooter
            onSave={handleSave}
            onUndo={handleUndo}
            saving={dealUpdateLoading}
            visible={hasChanges && !isSaveButtonVisible}
          />
        )}
        {deal.accessStatus?.update && <HistoryButton entity={deal} entityType={EntityType.DEAL} />}
      </div>
    </TabLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  allOrgUsers: getUsersOfEntireOrganization(state),
  allUsersAllowedForOwnership:
    getOrganizationSettingValue(state)(OrganizationSetting.ALLOW_OWNERSHIP_TO_ALL_USERS, {
      [EntityType.DEAL]: false,
    })?.[EntityType.DEAL] ?? false,
  closedDateUpdateSetting: getOrganizationSettingValue(state)(
    OrganizationSetting.CLOSED_DATE_UPDATE_PREFERENCE
  ),
  deleting: isDeleting(state),
  entityRelatingSuccess: isEntityRelatingSuccessful(state),
  funnels: getFunnels(state),
  funnelStages: getFunnelStages(state),
  isEntityRelating: isEntityRelating(state),
  isOwner: isCurrentUserOwner(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  lossReasons: getDealLossReasons(state),
});

const mapDispatchToProps = {
  ensureStagesFetched: ensureStagesFetched.request,
  fetchDealLossReasons: fetchDealLossReasons.request,
  onChangeAssociatedEntities: changeAssociatedEntities.request,
  onDelete: deleteDeal.request,
  onUpdateDeal: updateDeal.request,
  relateUnrelateEntities: relateUnrelateEntities.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Details);
